import React, { useEffect, useRef, useState } from "react";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import * as Yup from "yup";
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
  Table,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import "./style.scss";
import { TripModal } from "../../Components/Common/TripModal/tripModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import { Pagination } from "../../Components/Common/Pagination";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  createSalary,
  deleteSalary,
  getAllSalaryList,
  getSalaryById,
  updateSalary,
  getAllDriverList,
} from "../../slices/thunks";
import {
  hideErrorMessage,
  loadingData,
} from "../../slices/salaryManagement/reducer";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";

const SalaryManagement = () => {
  const formikRef = useRef(null);
  const dispatch = useDispatch();

  const salaryList = useSelector((state) => state.Salary.salaryList);
  const salaryById = useSelector((state) => state.Salary.salaryById);
  const loading = useSelector((state) => state.Salary.loading);

  const error = useSelector((state) => state.Salary.error);
  const errorMessage = useSelector((state) => state.Salary.errorMessage);
  const driverList = useSelector(
    (state) => state.DriverManagementSlice.driverList
  );

  const [viewDetails, setViewDetails] = useState(false);
  const [addSalaryModal, setAddSalaryModal] = useState(false);
  const [deletSalaryModal, setDeletSalaryModal] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toasterType, setToasterType] = useState("success");
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const toggleViewDetailModal = () => {
    setViewDetails(!viewDetails);
  };

  const toggleAddSalaryModal = async () => {
    setAddSalaryModal(!addSalaryModal);
  };

  const toggleDeleteSalaryModal = async () => {
    setDeletSalaryModal(!deletSalaryModal);
  };

  const salarySchema = Yup.object().shape({
    driver: Yup.number().required("Driver is required"),
    hours_worked: Yup.string().required("Worked Hours are required"),
    salary_status: Yup.string().required("Payment Status is required"),
    trips_completed: Yup.string().required("Trips Completed are required"),
  });

  const addInitialValues = {
    driver: null,
    hours_worked: null,
    rate_per_hour: null,
    use_fixed_rate_per_hour: true,
    custom_fixed_rate: null,
    trips_completed: null,
    month: null,
    salary_status: true,
  };

  const updateInitialValues = {
    driver: salaryById?.driver,
    hours_worked: salaryById?.hours_worked,
    rate_per_hour: salaryById?.rate_per_hour,
    use_fixed_rate_per_hour: salaryById?.use_fixed_rate_per_hour,
    custom_fixed_rate: salaryById?.custom_fixed_rate,
    trips_completed: salaryById?.trips_completed,
    month: salaryById?.month ? moment(salaryById.month).toDate() : null,
    salary_status: salaryById?.salary_status,
  };

  const handleSalarySubmit = async (values) => {
    try {
      let response;
      if (!isAdd) {
        response = await dispatch(updateSalary(salaryById.id, values));
      } else {
        response = await dispatch(createSalary(values));
      }
      const { isSuccess: success, message } = response;
      showToaster(success, message);
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleAddSalaryModal();
      await dispatch(getAllSalaryList(currentPage, pageSize, searchText));
    }
  };

  const handleDeleteSalary = async () => {
    try {
      const response = await dispatch(deleteSalary(salaryById.id));
      const { isSuccess: success, message } = response;
      showToaster(success, message);
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleDeleteSalaryModal();
      await dispatch(getAllSalaryList(currentPage, pageSize, searchText));
    }
  };

  useEffect(() => {
    try {
      dispatch(getAllDriverList("", "", ""));
    } catch (error) {}
  }, [dispatch]);

  useEffect(() => {
    try {
      const delayDebounceFn = setTimeout(async () => {
        dispatch(getAllSalaryList(currentPage, pageSize, searchText));
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    } catch (error) {}
  }, [dispatch, currentPage, pageSize, searchText]);

  document.title = "Salary Management | D&A";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-column gap-4">
            <TitleBar title={"Salary Management"} />

            <div className="userManagementSection">
              <div className="d-flex flex-column-reverse flex-lg-row justify-content-end align-items-end p-2 p-lg-0 gap-1 gap-lg-0">
                <InputGroup className="search-input">
                  <InputGroupText>
                    <i className="ri-search-line" />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search input text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    disabled={salaryList.count === 0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        dispatch(getAllSalaryList(1, pageSize, searchText));
                        setCurrentPage(1);
                      }
                    }}
                  />
                </InputGroup>
                <Button
                  onClick={() => {
                    setIsAdd(true);
                    toggleAddSalaryModal();
                    dispatch(loadingData({ type: "read", value: false }));
                  }}
                  className="newUserManagementBtn"
                >
                  Add Driver Salary <i className="ri-add-line" />
                </Button>
              </div>

              <div
                className="position-fixed top-0 end-0 p-3"
                style={{ zIndex: 1055 }}
              >
                <Toast isOpen={showToast}>
                  <ToastHeader
                    icon={error ? "danger" : "success"}
                    toggle={() => setShowToast(false)}
                  >
                    {error ? "Error" : "Success"}
                  </ToastHeader>
                  <ToastBody>{toastMessage}</ToastBody>
                </Toast>
              </div>

              <div className="trip-table">
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Driver Name</th>
                      <th>Hours Worked</th>
                      <th>Rate(£)</th>
                      <th>Total Salary (£) </th>
                      <th>Month</th>
                      <th>Payment Status</th>

                      <th>Action</th>
                    </tr>
                  </thead>
                  {loading.read ? (
                    <tbody>
                      <tr>
                        <td colSpan="8" className="text-center align-middle">
                          <Spinner color="primary" />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {salaryList?.results?.length ? (
                        salaryList?.results?.map((element, index) => (
                          <tr key={index}>
                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                            <td>
                              {element.driver_data?.first_name}{" "}
                              {element.driver_data?.last_name}
                            </td>
                            <td>{element.hours_worked || "-"}</td>
                            <td>
                              {element.rate_per_hour ||
                                element.custom_fixed_rate ||
                                "-"}
                            </td>
                            <td>{element.calculate_salary || "-"}</td>
                            <td>
                              {moment(element.month).format("MMMM YYYY") || "-"}
                            </td>
                            <td>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "80px",
                                  textAlign: "center",
                                  backgroundColor: element.salary_status
                                    ? "#28a74533"
                                    : "#dc354533",
                                  padding: "4px 8px",
                                  borderRadius: "10px",
                                  color: element.salary_status
                                    ? "#28a745"
                                    : "#dc3545",
                                  fontWeight: 500,
                                }}
                              >
                                {element.salary_status ? "Paid" : "Pending"}
                              </span>
                            </td>

                            <td>
                              <div className="action-icons">
                                <i
                                  className="ri-eye-fill icon"
                                  style={{ color: "#E8A014" }}
                                  onClick={() => {
                                    toggleViewDetailModal();
                                    dispatch(
                                      loadingData({
                                        type: "read",
                                        value: false,
                                      })
                                    );
                                    dispatch(getSalaryById(element.id));
                                  }}
                                />
                                <i
                                  className="ri-pencil-fill icon"
                                  style={{ color: "#007AFF" }}
                                  onClick={() => {
                                    setIsAdd(false);
                                    toggleAddSalaryModal();
                                    dispatch(
                                      loadingData({
                                        type: "read",
                                        value: false,
                                      })
                                    );
                                    dispatch(getSalaryById(element.id));
                                  }}
                                />
                                <i
                                  className="ri-delete-bin-fill icon"
                                  style={{ color: "#FB0000" }}
                                  onClick={() => {
                                    toggleDeleteSalaryModal();
                                    dispatch(
                                      loadingData({
                                        type: "read",
                                        value: false,
                                      })
                                    );
                                    dispatch(getSalaryById(element.id));
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="text-start text-sm-center" colSpan={9}>
                            <h3>No Salary Found</h3>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </Table>
              </div>
              {salaryList?.results?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  totalItems={salaryList?.count || 0}
                />
              )}
            </div>
          </div>

          <TripModal
            isModalOpen={viewDetails}
            toggleModal={toggleViewDetailModal}
            buttonText="Assign"
            closeText={"Close without saving"}
            title={"Salary Management"}
            tripText={"Driver Salary Details"}
            showButtons={false}
            showDeleteIcon={false}
            showEditIcon={false}
            showcloseIcon={true}
            editIconClick={() => {
              toggleViewDetailModal();
              dispatch(getSalaryById(salaryById.id));
              setIsAdd(false);
              toggleAddSalaryModal();
              dispatch(loadingData({ type: "read", value: false }));
            }}
            deleteIconClick={() => {
              toggleViewDetailModal();
              dispatch(getSalaryById(salaryById.id));
              toggleDeleteSalaryModal();
              dispatch(loadingData({ type: "read", value: false }));
            }}
          >
            <div className="px-3 d-flex flex-column gap-4">
              <Row className="mb-5">
                <div className="custom-box-value">
                  <div className="d-flex flex items-center justify-space-between gap-3">
                    <span className="userDetailLabel">Driver Name</span>
                    <span className="userDetailValue">
                      {" "}
                      {salaryById.driver_data?.first_name}{" "}
                      {salaryById.driver_data?.last_name}
                    </span>
                  </div>
                </div>

                {salaryById?.use_fixed_rate_per_hour === true ? (
                  <div className="custom-box-value">
                    <div className="d-flex flex items-center justify-space-between gap-3">
                      <span className="userDetailLabel">Fixed Rate</span>
                      <span className="userDetailValue">
                        {" "}
                        {salaryById.custom_fixed_rate || "-"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="custom-box-value">
                    <div className="d-flex flex items-center justify-space-between gap-3">
                      <span className="userDetailLabel">Rate Per Hour</span>
                      <span className="userDetailValue">
                        {" "}
                        {salaryById?.rate_per_hour || "-"}
                      </span>
                    </div>
                  </div>
                )}

                <div className="custom-box-value">
                  <div className="d-flex flex items-center justify-space-between gap-3">
                    <span className="userDetailLabel">Total Salary</span>
                    <span className="userDetailValue">
                      {" "}
                      {salaryById.calculate_salary || "-"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex items-center justify-space-between gap-3">
                    <span className="userDetailLabel">Hours Worked </span>
                    <span className="userDetailValue">
                      {" "}
                      {salaryById?.hours_worked || "-"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex items-center justify-space-between gap-3">
                    <span className="userDetailLabel">Month</span>
                    <span className="userDetailValue">
                      {" "}
                      {moment(salaryById?.month).format("MMMM YYYY") || "-"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex items-center justify-space-between gap-3">
                    <span className="userDetailLabel">
                      Total Completed Trips
                    </span>
                    <span className="userDetailValue">
                      {" "}
                      {salaryById?.trips_completed || "-"}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    marginTop: "16px",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    marginBottom: "0px",
                  }}
                >
                  <div className="d-flex flex items-center justify-space-between gap-3">
                    <span className="userDetailLabel">Payment Status </span>
                    <span className="userDetailValue">
                      {" "}
                      <span
                        style={{
                          display: "inline-block",
                          width: "80px",
                          textAlign: "center",
                          backgroundColor: salaryById?.salary_status
                            ? "#28a74533"
                            : "#dc354533",
                          padding: "4px 8px",
                          borderRadius: "10px",
                          color: salaryById?.salary_status
                            ? "#28a745"
                            : "#dc3545",
                          fontWeight: 500,
                        }}
                      >
                        {salaryById?.salary_status ? "Paid" : "Pending"}
                      </span>
                    </span>
                  </div>
                </div>
              </Row>
            </div>
          </TripModal>

          <TripModal
            isModalOpen={addSalaryModal}
            toggleModal={toggleAddSalaryModal}
            buttonText={isAdd ? "Add Salary" : "Update Salary"}
            closeText={"Close without saving"}
            title={"Salary Management"}
            tripText={isAdd ? "Add Driver Salary" : "Update Driver Salary"}
            size="lg"
            loading={isAdd ? loading.create : loading.update}
            showcloseIcon={true}
            onSubmit={() => {
              if (formikRef.current) {
                formikRef.current.handleSubmit();
              }
            }}
          >
            <div className="px-3 d-flex gap-4">
              <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={isAdd ? addInitialValues : updateInitialValues}
                validationSchema={salarySchema}
                onSubmit={handleSalarySubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => {
                  const handleDriverIdChange = (e) => {
                    const selectedId = e.target.value;
                    setFieldValue("driver", selectedId);

                    const selectedUser = driverList?.results?.find(
                      (user) => user.id === Number(selectedId)
                    );
                    if (selectedUser) {
                      setFieldValue("driver", selectedUser.id);
                    }
                  };
                  return (
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="driver" className="passwordLabel">
                              Driver
                            </Label>
                            <Input
                              type="select"
                              name="driver"
                              className="profileInput"
                              id="driver"
                              value={values.driver}
                              onChange={handleDriverIdChange}
                              onBlur={handleBlur}
                              invalid={
                                touched.driver && errors.driver ? true : false
                              }
                            >
                              <option value="">Select Driver</option>
                              {driverList?.map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.first_name} ({user.id})
                                </option>
                              ))}
                            </Input>
                            {touched.driver && errors.driver && (
                              <FormFeedback type="invalid">
                                {errors.driver}
                              </FormFeedback>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md={6}>
                          <FormGroup>
                            <Label for="hours_worked" className="passwordLabel">
                              Hours Worked
                            </Label>
                            <Input
                              type="number"
                              name="hours_worked"
                              className="profileInput"
                              id="hours_worked"
                              placeholder="Enter Hours Worked"
                              value={values.hours_worked}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              onWheel={(e) => e.target.blur()}
                              invalid={
                                touched.hours_worked && errors.hours_worked
                                  ? true
                                  : false
                              }
                            />
                            {touched.hours_worked && errors.hours_worked ? (
                              <FormFeedback type="invalid">
                                {errors.hours_worked}
                              </FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>

                        <Col md={6}>
                          <FormGroup>
                            <Label for="month" className="passwordLabel">
                              Monthly Salary
                            </Label>
                            <DatePicker
                              className="profileInput"
                              selected={values.month}
                              value={values.month}
                              onChange={(date) => {
                                console.log("date", date);

                                formikRef.current.setFieldValue("month", date);
                              }}
                              dateFormat="MMMM YYYY"
                              showMonthYearPicker
                              placeholderText="Select month and year"
                            />
                          </FormGroup>
                        </Col>

                        <Col md={6}>
                          <FormGroup>
                            <Label
                              for="salary_status"
                              className="passwordLabel"
                            >
                              Salary Status
                            </Label>
                            <Input
                              type="select"
                              name="salary_status"
                              className="profileInput"
                              id="salary_status"
                              value={values.salary_status}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={
                                touched.salary_status && errors.salary_status
                                  ? true
                                  : false
                              }
                            >
                              <option value="">Select Salary Status</option>

                              <option value={true}>Paid</option>
                              <option value={false}>Pending</option>
                            </Input>
                            {touched.salary_status && errors.salary_status ? (
                              <FormFeedback type="invalid">
                                {errors.salary_status}
                              </FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>

                        <Col md={6}>
                          <FormGroup>
                            <Label
                              for="trips_completed"
                              className="passwordLabel"
                            >
                              Trips Completed
                            </Label>
                            <Input
                              type="number"
                              name="trips_completed"
                              className="profileInput"
                              id="trips_completed"
                              placeholder="Enter Hours Worked"
                              value={values.trips_completed}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              onWheel={(e) => e.target.blur()}
                              invalid={
                                touched.trips_completed &&
                                errors.trips_completed
                                  ? true
                                  : false
                              }
                            />
                            {touched.trips_completed &&
                            errors.trips_completed ? (
                              <FormFeedback type="invalid">
                                {errors.trips_completed}
                              </FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>

                        <Col md={12}>
                          <FormGroup className="d-flex">
                            <Label className="rateTypeLabel">
                              <Input
                                type="radio"
                                name="select_rate_type"
                                id="fixed_rate"
                                value="fixed"
                                checked={
                                  values.use_fixed_rate_per_hour === true
                                }
                                onBlur={handleBlur}
                                onChange={() =>
                                  setFieldValue("use_fixed_rate_per_hour", true)
                                }
                                className="rateTypeRadio"
                              />
                              Fixed Rate
                            </Label>

                            <Label className="rateTypeLabel">
                              <Input
                                type="radio"
                                name="select_rate_type"
                                id="variable_rate"
                                value="variable"
                                checked={
                                  values.use_fixed_rate_per_hour === false
                                }
                                onBlur={handleBlur}
                                onChange={() =>
                                  setFieldValue(
                                    "use_fixed_rate_per_hour",
                                    false
                                  )
                                }
                                className="rateTypeRadio"
                              />
                              Rate Per Hour
                            </Label>

                            {touched.use_fixed_rate_per_hour &&
                            errors.use_fixed_rate_per_hour ? (
                              <FormFeedback type="invalid">
                                {errors.use_fixed_rate_per_hour}
                              </FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>

                        {values.use_fixed_rate_per_hour === false ? (
                          <>
                            <Col md={6}>
                              <FormGroup>
                                <Label
                                  for="rate_per_hour"
                                  className="passwordLabel"
                                >
                                  Rate Per Hour
                                </Label>
                                <Input
                                  type="text"
                                  name="rate_per_hour"
                                  className="profileInput"
                                  id="rate_per_hour"
                                  placeholder="Enter Rate Per Hour"
                                  value={values.rate_per_hour}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="alias" className="passwordLabel">
                                  Total Salary
                                </Label>
                                <Input
                                  type="text"
                                  name="total_salary"
                                  disabled
                                  className="profileInput"
                                  id="total_salary"
                                  placeholder="Enter Total Salary"
                                  value={
                                    values.hours_worked * values.rate_per_hour
                                  }
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                              </FormGroup>
                            </Col>
                          </>
                        ) : (
                          <>
                            <Col md={6}>
                              <FormGroup>
                                <Label
                                  for="custom_fixed_rate"
                                  className="passwordLabel"
                                >
                                  Fixed Rate
                                </Label>
                                <Input
                                  type="number"
                                  name="custom_fixed_rate"
                                  className="profileInput"
                                  id="custom_fixed_rate"
                                  placeholder="Enter Fixed Rate"
                                  value={values.custom_fixed_rate}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="alias" className="passwordLabel">
                                  Total Salary
                                </Label>
                                <Input
                                  type="number"
                                  name="total_salary"
                                  disabled
                                  className="profileInput"
                                  id="total_salary"
                                  placeholder="Enter Total Salary"
                                  value={values.custom_fixed_rate}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                              </FormGroup>
                            </Col>
                          </>
                        )}
                      </Row>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </TripModal>

          <DeleteModal
            show={deletSalaryModal}
            loading={loading.delete}
            onDeleteClick={() => {
              dispatch(loadingData({ type: "read", value: false })),
                handleDeleteSalary();
            }}
            onCloseClick={toggleDeleteSalaryModal}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default SalaryManagement;
